import type { GHAScopeRuleCugTier } from '~/services/ConnectionGoogleService';

import { GHA_SUPPLIER_LABELS } from './constants';

const TIER_SHORT_LABELS: Record<string, string> = {
  tier_1: 'Tier 1',
  tier_2: 'Tier 2',
  tier_3: 'Tier 3',
  tier_4: 'Tier 4',
};

const supplierLabel = (supplier: string): string => GHA_SUPPLIER_LABELS[supplier] ?? supplier;
const tierLabel = (tier: string): string => TIER_SHORT_LABELS[tier] ?? tier;

/** Multi-value dimensions: values within one dimension are OR, so they join with "/". */
export interface GHAScopeCriteria {
  suppliers: Array<string> | null;
  /** API-resolved display names, preferred over the FE label map when present. */
  supplierNames?: Array<string> | null;
  chainNames: Array<string> | null;
  cugTiers: Array<GHAScopeRuleCugTier | string> | null;
  userCountries: Array<string> | null;
}

/** "Expedia/Agoda · Tier 2/Tier 3 · chain: Hilton/Accor · GB/DE", or "Everything" when every
 *  dimension is open. */
export function formatScopeCriteria(rule: GHAScopeCriteria): string {
  const parts: Array<string> = [];
  if (rule.suppliers != null) {
    // Prefer the names the API resolved; the local map only labels the option lists.
    parts.push((rule.supplierNames ?? rule.suppliers.map(supplierLabel)).join('/'));
  }
  if (rule.cugTiers != null) {
    parts.push(rule.cugTiers.map(tierLabel).join('/'));
  }
  if (rule.chainNames != null) {
    parts.push(`chain: ${rule.chainNames.join('/')}`);
  }
  if (rule.userCountries != null) {
    parts.push(rule.userCountries.join('/'));
  }
  return parts.length === 0 ? 'Everything' : parts.join(' · ');
}

export function formatActor(actor: string | null): string {
  return actor ?? '—';
}

export function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString();
}

/** Full sentence for the exclusions grid, e.g. "Expedia or Agoda: do not discount Tier 2 or
 *  Tier 3 properties on any chain, for every viewer country". */
export function describeExclusion(rule: GHAScopeCriteria): string {
  const who = rule.suppliers == null ? 'All suppliers' : rule.suppliers.map(supplierLabel).join(' or ');
  const tier = rule.cugTiers == null ? 'any tier' : rule.cugTiers.map(tierLabel).join(' or ');
  const chain = rule.chainNames == null ? 'on any chain' : `on chain ${rule.chainNames.join(' or ')}`;
  const country =
    rule.userCountries == null ? 'for every viewer country' : `for ${rule.userCountries.join('/')} viewers`;
  return `${who}: do not discount ${tier} properties ${chain}, ${country}`;
}

// ---------------------------------------------------------------------------
// Pure logic extracted from the panels/dialogs so it can be unit-tested.
// ---------------------------------------------------------------------------

export interface ScopeDimensionsFormState {
  suppliers: Array<string>;
  chainNames: Array<string>;
  cugTiers: Array<GHAScopeRuleCugTier>;
  userCountries: Array<string>;
}

export interface NormalisedScopeDimensions {
  suppliers?: Array<string>;
  chainNames?: Array<string>;
  cugTiers?: Array<GHAScopeRuleCugTier>;
  userCountries?: Array<string>;
  /** Set = validation failed; the dimension fields are absent. */
  error?: string;
}

/** Trims, dedupes and validates the shared scope dimensions; an empty list becomes an omitted
 *  key (the API's "match everything"). Countries are uppercased and must be 2-letter ISO. */
export function normaliseScopeDimensions(form: ScopeDimensionsFormState): NormalisedScopeDimensions {
  const countries = [
    ...new Set(form.userCountries.map((country) => country.trim().toUpperCase()).filter((country) => country !== '')),
  ];
  const chains = [...new Set(form.chainNames.map((chain) => chain.trim()).filter((chain) => chain !== ''))];
  const badCountry = countries.find((country) => !/^[A-Z]{2}$/.test(country));
  if (badCountry != null) {
    return { error: `Viewer country '${badCountry}' must be a 2-letter ISO code` };
  }
  return {
    suppliers: form.suppliers.length === 0 ? undefined : form.suppliers,
    chainNames: chains.length === 0 ? undefined : chains,
    cugTiers: form.cugTiers.length === 0 ? undefined : form.cugTiers,
    userCountries: countries.length === 0 ? undefined : countries,
  };
}

/** The full intended ordering after moving one rule up/down within its account, or null when the
 *  move falls off either end (or the rule is not in the enabled set). */
export function moveRule(
  rules: Array<{ id: string; enabled: boolean; accountType: string; priority: number }>,
  rule: { id: string; accountType: string },
  direction: -1 | 1,
): Array<string> | null {
  const accountRules = rules
    .filter((candidate) => candidate.enabled && candidate.accountType === rule.accountType)
    .sort((a, b) => a.priority - b.priority);
  const index = accountRules.findIndex((candidate) => candidate.id === rule.id);
  const target = index + direction;
  if (index === -1 || target < 0 || target >= accountRules.length) {
    return null;
  }
  const orderedIds = accountRules.map((candidate) => candidate.id);
  [orderedIds[index], orderedIds[target]] = [orderedIds[target], orderedIds[index]];
  return orderedIds;
}
